import React, { useState, useContext } from "react";
import "../css/Mitglieddaten.css";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { exemplarApi } from "../api/exemplarApi";
import { removeAusleihe, removeReservierung } from "../redux/userSlice";
import { userApi } from "../api/userApi";
import { AuthContext } from "../context/AuthContext";

export default function Mitglieddaten() {
  const user = useSelector((state: RootState) => state.user.user);
  const dispatch = useDispatch();
  const { fetchMe, logout } = useContext(AuthContext)!;

  // Edit State
  const [editMode, setEditMode] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: "",
    username: "",
    vorname: "",
    nachname: "",
    oldPassword: "",
    newPassword: "",
  });
  const [error, setError] = useState<string | null>(null);

  // --- Helper: Reset & Open ---
  const startEditing = (mode: string) => {
    setEditMode(mode);
    setError(null);
    if (user) {
      setFormData({
        email: user.email || "",
        username: user.username || "",
        vorname: user.vorname || "",
        nachname: user.nachname || "",
        oldPassword: "",
        newPassword: "",
      });
    }
  };

  const cancelEditing = () => {
    setEditMode(null);
    setError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // --- API Actions ---
  const handleSave = async (mode: string) => {
    try {
      setError(null);

      if (mode === "EMAIL") {
        await userApi.updateEmail(formData.email);
        alert("Email geändert!");
      } else if (mode === "USERNAME") {
        await userApi.updateUsername(formData.username);
        alert("Username geändert! Bitte neu einloggen.");
        await logout();
        return;
      } else if (mode === "DETAILS") {
        await userApi.updateDetails(formData.vorname, formData.nachname);
        alert("Daten aktualisiert!");
      } else if (mode === "PASSWORD") {
        if (!formData.oldPassword || !formData.newPassword) {
          setError("Bitte beide Felder ausfüllen.");
          return;
        }
        await userApi.updatePassword(
          formData.oldPassword,
          formData.newPassword,
        );
        alert("Passwort geändert!");
      }

      await fetchMe();
      cancelEditing();
    } catch (err: any) {
      const errMsg = err.response?.data?.message || "Fehler aufgetreten";
      setError(errMsg);
    }
  };

  // Ausleihe Logik (Unverändert)
  const handleReturn = async (exemplarId: number) => {
    try {
      await exemplarApi.zurueckgeben(exemplarId, user?.mitgliedID!);
      alert("Buch zurückgegeben!");
      dispatch(removeAusleihe(exemplarId));
      fetchMe();
    } catch (err: any) {
      alert("Fehler: " + err.message);
    }
  };

  const handleCancelReservation = async (exemplarId: number) => {
    try {
      await exemplarApi.reservierungAufheben(exemplarId, user?.mitgliedID!);
      alert("Reservierung aufgehoben!");
      dispatch(removeReservierung(exemplarId));
      fetchMe();
    } catch (err: any) {
      alert("Fehler: " + err.message);
    }
  };

  return (
    <div className="page-container">
      <h2 className="page-title">Meine Mitgliedsdaten</h2>

      {user ? (
        <div className="glass-card main-profile-card">
          <div className="welcome-header">
            <h3>
              Willkommen, {user.vorname} {user.nachname}
            </h3>
            <span className="badge">{user.mitgliedsstatus}</span>
          </div>

          <div className="info-grid">
            {/* 1. NAME (DETAILS) */}
            <div className="info-item full-width">
              <label>Name</label>
              <div className="value-row">
                <span>
                  {user.vorname} {user.nachname}
                </span>
                {editMode !== "DETAILS" && (
                  <button
                    className="edit-mini-btn"
                    onClick={() => startEditing("DETAILS")}
                  >
                    Ändern
                  </button>
                )}
              </div>

              {/* INLINE FORM: DETAILS */}
              {editMode === "DETAILS" && (
                <div className="inline-edit-box">
                  {error && <div className="inline-error">{error}</div>}
                  <div className="info-grid" style={{ marginBottom: "1rem" }}>
                    <div className="inline-form-group">
                      <label>Vorname</label>
                      <input
                        type="text"
                        name="vorname"
                        value={formData.vorname}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="inline-form-group">
                      <label>Nachname</label>
                      <input
                        type="text"
                        name="nachname"
                        value={formData.nachname}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="inline-actions">
                    <button
                      className="action-btn cancel"
                      onClick={cancelEditing}
                    >
                      Abbrechen
                    </button>
                    <button
                      className="action-btn return"
                      onClick={() => handleSave("DETAILS")}
                    >
                      Speichern
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 2. EMAIL */}
            <div className="info-item">
              <label>Email</label>
              <div className="value-row">
                <span>{user.email}</span>
                {editMode !== "EMAIL" && (
                  <button
                    className="edit-mini-btn"
                    onClick={() => startEditing("EMAIL")}
                  >
                    Ändern
                  </button>
                )}
              </div>

              {/* INLINE FORM: EMAIL */}
              {editMode === "EMAIL" && (
                <div className="inline-edit-box">
                  {error && <div className="inline-error">{error}</div>}
                  <div className="inline-form-group">
                    <label>Neue Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="inline-actions">
                    <button
                      className="action-btn cancel"
                      onClick={cancelEditing}
                    >
                      Abbrechen
                    </button>
                    <button
                      className="action-btn return"
                      onClick={() => handleSave("EMAIL")}
                    >
                      Speichern
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 3. USERNAME */}
            <div className="info-item">
              <label>Username</label>
              <div className="value-row">
                <span>{user.username}</span>
                {editMode !== "USERNAME" && (
                  <button
                    className="edit-mini-btn"
                    onClick={() => startEditing("USERNAME")}
                  >
                    Ändern
                  </button>
                )}
              </div>

              {/* INLINE FORM: USERNAME */}
              {editMode === "USERNAME" && (
                <div className="inline-edit-box">
                  {error && <div className="inline-error">{error}</div>}
                  <div className="inline-form-group">
                    <label>Neuer Username</label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                    />
                    <small className="inline-warning">
                      ⚠ Erfordert erneuten Login.
                    </small>
                  </div>
                  <div className="inline-actions">
                    <button
                      className="action-btn cancel"
                      onClick={cancelEditing}
                    >
                      Abbrechen
                    </button>
                    <button
                      className="action-btn return"
                      onClick={() => handleSave("USERNAME")}
                    >
                      Speichern
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 4. PASSWORT */}
            <div className="info-item">
              <label>Passwort</label>
              <div className="value-row">
                <span>********</span>
                {editMode !== "PASSWORD" && (
                  <button
                    className="edit-mini-btn"
                    onClick={() => startEditing("PASSWORD")}
                  >
                    Ändern
                  </button>
                )}
              </div>

              {/* INLINE FORM: PASSWORD */}
              {editMode === "PASSWORD" && (
                <div className="inline-edit-box">
                  {error && <div className="inline-error">{error}</div>}
                  <div className="inline-form-group">
                    <label>Altes Passwort</label>
                    <input
                      type="password"
                      name="oldPassword"
                      value={formData.oldPassword}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="inline-form-group">
                    <label>Neues Passwort</label>
                    <input
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="inline-actions">
                    <button
                      className="action-btn cancel"
                      onClick={cancelEditing}
                    >
                      Abbrechen
                    </button>
                    <button
                      className="action-btn return"
                      onClick={() => handleSave("PASSWORD")}
                    >
                      Speichern
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <hr className="divider" />

          {/* LIST SECTIONS (AUSLEIHEN & RESERVIERUNGEN) - Unverändert */}
          <section className="list-section">
            <h3>Aktive Ausleihen</h3>
            <div className="item-list">
              {user.ausleihen?.filter((a) => a.rueckgabeStatus !== "VERFUEGBAR")
                .length ? (
                user.ausleihen
                  .filter((a) => a.rueckgabeStatus !== "VERFUEGBAR")
                  .map((a) => (
                    <div key={a.ausleiheID} className="glass-item">
                      <div className="item-info">
                        <p className="item-title">Exemplar #{a.exemplarID}</p>
                        <p className="item-subtitle">
                          Status: {a.rueckgabeStatus}
                        </p>
                      </div>
                      <button
                        className="action-btn return"
                        onClick={() => handleReturn(a.exemplarID!)}
                      >
                        Zurückgeben
                      </button>
                    </div>
                  ))
              ) : (
                <p className="empty-text">Keine aktiven Ausleihen.</p>
              )}
            </div>
          </section>

          <section className="list-section">
            <h3>Reservierungen</h3>
            <div className="item-list">
              {user.reservierungen?.length ? (
                user.reservierungen.map((r) => (
                  <div key={r.exemplarID} className="glass-item">
                    <div className="item-info">
                      <p className="item-subtitle">
                        Reservierung #{r.reservierungID}
                      </p>
                    </div>
                    <button
                      className="action-btn cancel"
                      onClick={() => handleCancelReservation(r.exemplarID!)}
                    >
                      Aufheben
                    </button>
                  </div>
                ))
              ) : (
                <p className="empty-text">Keine Reservierungen.</p>
              )}
            </div>
          </section>
        </div>
      ) : (
        <div className="glass-card login-prompt">
          <p>Bitte einloggen.</p>
        </div>
      )}
    </div>
  );
}
